import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import Api from "../../api";
import DataMonitoring from "../../components/monitoring/DataMonitoring";
import { FaSignal, FaFilter, FaSearch, FaRedoAlt, FaChartBar } from "react-icons/fa";
import { toast } from 'react-toastify';
import { motion } from "framer-motion";
import { SipCalendar, SipDropdown } from "../../components/form/SipControls";

const Monitoring = () => {
  const navigate = useNavigate();
  const { gedung, getGedung, ruangan, getRuangan } = useData();
  const { currentUser } = useAuth();
  const [form, setForm] = useState({
    date: "",
    gedung_id: "",
    ruang: ""
  });
  const [showTable, setShowTable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [datamon, setDatamon] = useState([]);
  const [error, setError] = useState(null);
  const [monitoringResults, setMonitoringResults] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleForm = (e) => {
    const { name, value } = e.target;
    setForm(prevForm => {
      const updatedForm = { ...prevForm, [name]: value };

      if (name === 'gedung_id') {
        updatedForm.ruang = "";
      }

      if (name === 'date' && !value) {
        updatedForm.gedung_id = "";
        updatedForm.ruang = "";
      }

      return updatedForm;
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const clearDate = () => {
    setForm({
      date: "",
      gedung_id: "",
      ruang: ""
    });
    setShowTable(false);
    setDatamon([]);
    setError(null);
    setMonitoringResults([]);
  };

  const handleShowData = async () => {
    if (!form.ruang) {
      setError("Pilih ruangan terlebih dahulu");
      return;
    }

    if (!form.date) {
      setError("Pilih tanggal terlebih dahulu");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await Api.get(`/mon/inv/${form.ruang}`);
      if (res.data?.data && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setDatamon(res.data.data);
        setShowTable(true);
        setMonitoringResults([]);
      } else {
        setError("Data tidak ditemukan untuk ruangan yang dipilih");
        setDatamon([]);
        setShowTable(false);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || "Gagal memuat data. Silakan coba lagi.");
      setDatamon([]);
      setShowTable(false);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => {
    console.log('View item:', id);
  };

  const handleMonitoringDataChange = (data) => {
    setMonitoringResults(data);
  };

  const handleSubmitMonitoring = async () => {
    if (monitoringResults.length === 0) {
      setError("Tidak ada data monitoring yang dipilih untuk disimpan");
      return;
    }

    const invalidItems = monitoringResults.filter(item => {
      const hasValidKondisi = item.kondisi && item.kondisi.trim() !== '';
      const hasValidTanggal = (item.tanggal_monitoring && item.tanggal_monitoring.trim() !== '') || 
                             (form.date && form.date.trim() !== '');
      return !hasValidKondisi || !hasValidTanggal;
    });

    if (invalidItems.length > 0) {
      console.log('Invalid items:', invalidItems);
      setError(`${invalidItems.length} item tidak memiliki kondisi atau tanggal monitoring yang valid. Pastikan semua item telah diisi dengan benar.`);
      return;
    }

    const dataToSubmit = monitoringResults.map(item => ({
      ...item,
      tanggal_monitoring: item.tanggal_monitoring || form.date,
      petugas: item.petugas || currentUser?.name || 'Unknown'
    }));

    setSubmitLoading(true);
    setError(null);
    try {
      const response = await Api.post('/mon/storemon', dataToSubmit);
      if (response.data?.message ==='success') {
        toast.success('Data monitoring berhasil disimpan!', {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setMonitoringResults([]);
        await handleShowData();
      } else {
        toast.error("Data sudah ada", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error('Error submitting monitoring data:', err);
      const errorMessage = err.response?.data?.message || "Gagal menyimpan data monitoring. Silakan coba lagi.";
      setError(errorMessage);
      toast.error("data sudah ada", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    const loadGedung = async () => {
      try {
        await getGedung();
      } catch (error) {
        console.error('Failed to load gedung:', error);
        setError('Gagal memuat data gedung');
      }
    };
    loadGedung();
  }, []);

  useEffect(() => {
    const loadRuangan = async () => {
      if (form.gedung_id) {
        try {
          await getRuangan(form.gedung_id);
        } catch (error) {
          console.error('Failed to load ruangan:', error);
          setError('Gagal memuat data ruangan');
        }
      } else {
        setForm(prev => ({ ...prev, ruang: "" }));
      }
    };
    loadRuangan();
  }, [form.gedung_id]);

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-primary px-4 py-5 sm:px-6 sm:py-6 md:px-8 md:py-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
              <div className="text-white">
                <div className="flex items-center gap-2 sm:gap-3 mb-1.5 sm:mb-2">
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="p-1.5 sm:p-2 bg-white/20 rounded-lg"
                  >
                    <FaSignal className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                  <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Monitoring Inventaris</h1>
                </div>
                <p className="text-primary text-xs sm:text-sm md:text-base">
                  Pantau dan kelola inventaris gedung secara real-time
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleBack}
                className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-2.5 sm:py-3 rounded-xl hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span className="font-medium">Kembali</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Filter Section */}
        <div className="min-w-0 max-w-full overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg sm:rounded-2xl">
          <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FaFilter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Filter Monitoring
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Pilih kriteria untuk memfilter data monitoring</p>
          </div>

          <div className="p-4 sm:p-5 md:p-6">
            {/* 1 kolom sampai layar cukup untuk 3 field (iPad mini portrait tetap penuh) */}
            <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-3 xl:gap-6">
              {/* Date Input */}
              <div className="min-w-0 max-w-full space-y-1.5 sm:space-y-2">
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                  Tanggal Monitoring
                </label>
                <SipCalendar
                  name="date"
                  value={form.date}
                  onChange={handleForm}
                  placeholder="Pilih tanggal monitoring"
                />
                {!form.date && (
                  <p className="mt-1 text-xs text-gray-500">
                    Pilih tanggal untuk melanjutkan
                  </p>
                )}
              </div>

              <div className="min-w-0 max-w-full space-y-1.5 sm:space-y-2">
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                  Gedung
                </label>
                <SipDropdown
                  name="gedung_id"
                  value={form.gedung_id ? String(form.gedung_id) : ""}
                  onChange={handleForm}
                  disabled={!form.date}
                  placeholder={!form.date ? "Pilih tanggal terlebih dahulu..." : "Pilih Gedung..."}
                  options={(gedung || []).map((item) => ({
                    label: item.nama_gedung || item.gedung,
                    value: String(item.id),
                  }))}
                />
                {form.date && !form.gedung_id && (
                  <p className="mt-1 text-xs text-gray-500">
                    Pilih gedung untuk melanjutkan
                  </p>
                )}
              </div>

              <div className="min-w-0 max-w-full space-y-1.5 sm:space-y-2">
                <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:mb-3 sm:text-sm">
                  Ruangan
                </label>
                <SipDropdown
                  name="ruang"
                  value={form.ruang}
                  onChange={handleForm}
                  disabled={!form.gedung_id}
                  placeholder={!form.gedung_id ? "Pilih gedung terlebih dahulu..." : "Pilih Ruangan..."}
                  options={(ruangan || []).map((item) => ({
                    label: item.ruang,
                    value: item.ruang,
                  }))}
                />
                {form.gedung_id && !form.ruang && (
                  <p className="mt-1 text-xs text-gray-500">
                    Pilih ruangan untuk melihat detail
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {form.date && form.gedung_id && form.ruang && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mt-5 sm:mt-6 md:mt-8 pt-4 sm:pt-6 border-t border-gray-100"
              >
                <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:justify-center">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleShowData}
                    disabled={loading}
                    className="flex items-center justify-center gap-2 bg-primary text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {loading ? (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Memuat Data...
                      </>
                    ) : (
                      <>
                        <FaSearch className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Tampilkan Data
                      </>
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={clearDate}
                    className="flex items-center justify-center gap-2 bg-neutral text-gray-700 px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-gray-300 transition-all duration-300 font-medium text-sm sm:text-base"
                  >
                    <FaRedoAlt className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Reset Filter
                  </motion.button>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Ringkasan Data Monitoring */}
        {showTable && datamon.length > 0 && monitoringResults.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-secondary/50 border-b border-secondary">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Ringkasan Data Monitoring
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">Data yang dipilih untuk monitoring</p>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-3 gap-2 sm:gap-4">
                <div className="bg-success/10 rounded-lg p-3 sm:p-4">
                  <div className="text-lg sm:text-2xl font-bold text-success">
                    {monitoringResults.filter(item => item.kondisi === 'baik').length}
                  </div>
                  <div className="text-[11px] sm:text-sm text-gray-600 leading-tight">Kondisi Baik</div>
                </div>
                <div className="bg-danger/10 rounded-lg p-3 sm:p-4">
                  <div className="text-lg sm:text-2xl font-bold text-danger">
                    {monitoringResults.filter(item => item.kondisi === 'rusak').length}
                  </div>
                  <div className="text-[11px] sm:text-sm text-gray-600 leading-tight">Kondisi Rusak</div>
                </div>
                <div className="bg-info/10 rounded-lg p-3 sm:p-4">
                  <div className="text-lg sm:text-2xl font-bold text-info">
                    {monitoringResults.length}
                  </div>
                  <div className="text-[11px] sm:text-sm text-gray-600 leading-tight">Total Dipilih</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Data Table Section */}
        {showTable && datamon.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                <div>
                  <h2 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaChartBar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Data Monitoring Inventaris
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1">
                    Tanggal: {form.date} | Gedung: {gedung?.find(g => g.id === parseInt(form.gedung_id))?.nama_gedung} | Ruangan: {form.ruang}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-600">Total: {datamon.length} item</span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <DataMonitoring
                datamon={datamon}
                handleView={handleView}
                petugas={currentUser?.name}
                onMonitoringDataChange={handleMonitoringDataChange}
                tanggalMonitoring={form.date}
              />
            </div>

            {/* Table Footer */}
            <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                <div className="text-xs sm:text-sm text-gray-600">
                  Menampilkan {datamon.length} dari {datamon.length} data |
                  <span className="font-semibold text-primary ml-1">
                    {monitoringResults.length} item dipilih
                  </span>
                </div>

                {monitoringResults.length > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.02, y: -1 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmitMonitoring}
                    disabled={submitLoading}
                    className="flex items-center justify-center gap-2 bg-accent text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl hover:bg-accent/90 transition-all duration-200 shadow-md font-medium disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {submitLoading ? (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Simpan Hasil Monitoring
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* No Data State */}
        {showTable && datamon.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-8 sm:p-12 text-center">
              <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold text-gray-800">Data Tidak Ditemukan</h3>
              <p className="mt-1 text-xs sm:text-sm text-gray-600">Tidak ada data inventaris yang cocok dengan filter yang Anda pilih.</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Monitoring;